import { ColorType, PageFormatType } from "@domain/enums/order.enums";
import { ServiceType } from "@domain/enums/machine.enums";
import { getInfoByService } from "@domain/enums/local/getInfoByService";
import { autorun, makeAutoObservable, observable, runInAction } from "mobx";
import { DisposableVm } from "@shared/utils/vm";
import { FileService } from "@domain/services/file.service";
import { OrderService } from "@domain/services/order.service";
import { HandleableError } from "@shared/utils/errors/handleableError";
import { debounce } from "@shared/utils/debounce";

/**
 * Состояния для заказа
 */

export interface FileState {
  id: string;
  name: string;
  previewUrl: string;
  originalUrl?: string;
  totalPages: number;
  
  // Настройки печати
  pagesRange: string;
  pagesRangeValid: string;
  colorType?: ColorType;
  duplexOption: boolean;
  formatType: PageFormatType;
  copiesCount: number;
  
  // Вычисляемые свойства
  price: number;
}

export interface ServiceState {
  id: string;
  type: ServiceType;
  title: string;
  files: FileState[];
  
  // Вычисляемые свойства
  totalPrice: number;
  totalFiles: number;
}

export interface CartState {
  services: ServiceState[];
  
  // Вычисляемые свойства
  totalPrice: number;
  totalServices: number;
  totalFiles: number;
}

/**
 * Класс для работы с состоянием заказа
 */
export class OrderState implements DisposableVm {
  cart: CartState = {
    services: [],
    totalPrice: 0,
    totalServices: 0,
    totalFiles: 0
  };
  
  editing: {
    service: ServiceState | null;
    file: FileState | null;
  } = {
    service: null,
    file: null
  };
  
  machineId: number;
  filePrices: Record<string, number> = {};
  supportedServiceTypes: ServiceType[] = [];
  private _isCalculatingPrice = false;
  
  // Информация о расчете цены с проверкой ресурсов (без промокода)
  priceCalculation: {
    resourcesSufficient: boolean;
    missingResources: string[];
    resourcesDetails: string;
  } | null = null;
  
  // Информация о расчете корзины с промокодом и наградами
  cartCalculation: {
    totalPrice: number;
    originalPrice: number;
    discount: number;
    promoCode: {
      code: string;
      description: string;
      discount_type: string;
      discount_value: number;
    } | null;
    freePrintsUsed: Record<string, number>;
    freePrintsAvailable: Record<string, number>;
    freePrintsDiscount: number;
    pointsUsed: number;
    pointsAvailable: number;
    minCardPayment: number;
  } | null = null;

  // Чем человек согласен платить. По умолчанию награды применяются: он их
  // заработал, и спрашивать каждый раз разрешения потратить своё незачем.
  // Отказаться можно — бесплатный отпечаток иногда хочется приберечь.
  payWith: { freePrints: boolean; points: boolean } = { freePrints: true, points: true };

  /** Переключить способ оплаты и пересчитать корзину. */
  setPayWith(next: Partial<{ freePrints: boolean; points: boolean }>): void {
    runInAction(() => {
      this.payWith = { ...this.payWith, ...next };
    });
    void this.calculateCartWithPromo();
  }
  
  autoruns: Array<() => void> = [];
  private _isRestoringState = false;
  private saveStateTimeout: ReturnType<typeof setTimeout> | null = null;
  private debouncedCalculatePrice: () => Promise<void>;
  private previousPriceParams: string | null = null; // Для отслеживания изменений параметров
  
  private readonly STORAGE_KEY_PREFIX = "order_state_";
  
  // Геттеры для совместимости со старым API
  get services() {
    return this.cart.services;
  }
  
  get currentService() {
    return this.editing.service;
  }
  
  get currentFile() {
    return this.editing.file;
  }
  
  get totalPrice() {
    return this.cart.totalPrice;
  }
  
  get filePrice() {
    return this.filePrices;
  }
  
  // Сеттеры для совместимости со старым API
  set currentService(service: ServiceState | null) {
    this.editing.service = service;
  }
  
  set currentFile(file: FileState | null) {
    this.editing.file = file;
  }
  
  constructor(machineId: number) {
    this.machineId = machineId;
    // Исключаем приватные флаги из observable, так как они изменяются вне action контекста
    makeAutoObservable(this, {}, { 
      _isCalculatingPrice: false,
      _isRestoringState: false,
      saveStateTimeout: false,
      autoruns: false,
      debouncedCalculatePrice: false,
      previousPriceParams: false
    });
    
    // Создаем debounced версию calculatePrice с задержкой 500ms
    // Это предотвратит спам запросов при быстром изменении параметров
    this.debouncedCalculatePrice = debounce(() => {
      return this.calculatePrice();
    }, 500);
    
    // Восстанавливаем состояние из localStorage
    this.restoreState();
    
    // Автоматический пересчет цены при изменении параметров файлов
    // (как в старом проекте telegram_mini_app)
    const disposeAutorun = autorun(() => {
      // Пропускаем если уже идет расчет или идет восстановление состояния
      if (this._isCalculatingPrice || this._isRestoringState) {
        return;
      }
      
      // Если нет редактируемого сервиса или файлов - не пересчитываем
      if (!this.editing.service || this.editing.service.files.length === 0) {
        this.previousPriceParams = null;
        return;
      }
      
      // Создаем строковое представление текущих параметров для сравнения
      // Отслеживаем только параметры, влияющие на цену:
      // - duplexOption, formatType, copiesCount, colorType, pagesRangeValid
      // - ID файлов и их количество (для отслеживания добавления/удаления)
      // Сортируем файлы по ID для стабильного сравнения
      const filesParams = this.editing.service.files
        .map((file) => ({
          fileId: String(file.id), // Убеждаемся, что ID строка
          duplexOption: Boolean(file.duplexOption),
          formatType: String(file.formatType || ""),
          copiesCount: Number(file.copiesCount || 1),
          colorType: file.colorType ?? null,
          pagesRangeValid: file.pagesRangeValid ?? null
        }))
        .sort((a, b) => a.fileId.localeCompare(b.fileId)); // Сортируем для стабильности
      
      const currentParams = JSON.stringify(filesParams);
      
      // Сравниваем с предыдущим значением - пересчитываем только если параметры действительно изменились
      if (this.previousPriceParams === currentParams) {
        return; // Параметры не изменились, не пересчитываем
      }
      
      // Сохраняем текущие параметры как предыдущие
      this.previousPriceParams = currentParams;
      
      // Параметры изменились - пересчитываем цену через debounced версию
      void this.debouncedCalculatePrice();
    });
    
    this.autoruns.push(disposeAutorun);
  }
  
  /**
   * Создает новую услугу для редактирования
   */
  createService(type: ServiceType): void {
    runInAction(() => {
      const info = getInfoByService(type);
      const newService: ServiceState = observable({
        id: String(Math.random()),
        type,
        title: info.title,
        files: [],
        totalPrice: 0,
        totalFiles: 0
      });
      
      this.editing.service = newService;
      this.editing.file = null;
    });
    
    this.saveState();
  }
  
  /**
   * Добавляет услугу (алиас для createService для совместимости)
   */
  addService(type: ServiceType): void {
    this.createService(type);
  }
  
  /**
   * Добавляет файлы в редактируемую услугу
   */
  async addFilesToEditingService(files: File[]): Promise<void> {
    if (!this.editing.service) {
      return;
    }
    
    if (files.length === 0) {
      return;
    }
    
    try {
      console.log("[OrderState.addFilesToEditingService] Starting upload, files count:", files.length);
      console.log("[OrderState.addFilesToEditingService] Current editing.service:", this.editing.service?.id, "files count:", this.editing.service?.files.length);
      
      // Загружаем файлы на сервер через FileService
      const result = await FileService.uploadFiles(files);
      
      console.log("[OrderState.addFilesToEditingService] Upload result structure:", {
        hasResult: !!result,
        hasData: !!result?.data,
        dataKeys: result?.data ? Object.keys(result.data) : [],
        fullData: result?.data
      });
      
      // FileService возвращает массив UploadedFile
      const uploadedFiles = result;
      
      console.log("[OrderState.addFilesToEditingService] Upload successful, received files:", uploadedFiles);
      
      if (!Array.isArray(uploadedFiles)) {
        console.error("[OrderState.addFilesToEditingService] uploadedFiles is not an array:", typeof uploadedFiles, uploadedFiles);
        throw new Error("Invalid file upload response format");
      }
      
      if (uploadedFiles.length !== files.length) {
        console.warn("[OrderState.addFilesToEditingService] Mismatch: uploaded", uploadedFiles.length, "files, expected", files.length);
      }
      
      // Получаем опции цвета и формата для текущего типа услуги (с бэка)
      const serviceType = this.editing.service.type;
      const info = getInfoByService(serviceType);
      const defaultColorType = info.colorOptions.length > 0 ? info.colorOptions[0].id : undefined;
      const defaultFormatType = (info.formatOptions[0] ?? "A4") as PageFormatType;
      
      runInAction(() => {
        // Создаем FileState объекты для каждого загруженного файла
        const newFiles: FileState[] = uploadedFiles.map((uploadedFile) => {
          // Преобразуем file_id в string (API может вернуть number, но доменная модель требует string)
          const fileId = String(uploadedFile.file_id);
          
          const fileState: FileState = observable({
            id: fileId,
            name: uploadedFile.original_filename,
            previewUrl: uploadedFile.preview_pdf_url,
            originalUrl: uploadedFile.original_url ?? undefined,
            totalPages: uploadedFile.pages_count,
            pagesRange: "",
            pagesRangeValid: "",
            colorType: defaultColorType,
            duplexOption: false,
            formatType: defaultFormatType,
            copiesCount: 1,
            price: 0
          });
          
          console.log("[OrderState.addFilesToEditingService] Created FileState:", fileState.id, fileState.name, "pages:", fileState.totalPages);
          return fileState;
        });
        
        console.log("[OrderState.addFilesToEditingService] Created", newFiles.length, "FileState objects");
        console.log("[OrderState.addFilesToEditingService] Before push - editing.service.files.length:", this.editing.service?.files.length);
        
        // Добавляем файлы в редактируемую услугу
        this.editing.service!.files.push(...newFiles);
        
        console.log("[OrderState.addFilesToEditingService] After push - editing.service.files.length:", this.editing.service?.files.length);
        
        // Если нет текущего файла, выбираем первый
        if (!this.editing.file && newFiles.length > 0) {
          this.editing.file = newFiles[0];
          console.log("[OrderState.addFilesToEditingService] Set currentFile to:", this.editing.file.id);
        }
        
        // Пересчитываем корзину
        this.recalculateCart();
      });
      
      console.log("[OrderState.addFilesToEditingService] Files added successfully, saving state");
      this.saveState();
      
      // Вызываем расчет цены сразу после добавления файлов
      // Autorun может не сработать сразу, если pagesRangeValid пустой
      // Поэтому вызываем calculatePrice() напрямую для немедленного расчета
      console.log("[OrderState.addFilesToEditingService] Triggering price calculation for new files");
      void this.calculatePrice();
    } catch (error) {
      console.error("[OrderState.addFilesToEditingService] Error uploading files:", error);
      console.error("[OrderState.addFilesToEditingService] Error details:", {
        name: error instanceof Error ? error.name : typeof error,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        errorCode: (error as any)?.code,
        errorDescription: (error as any)?.description,
      });
      throw error;
    }
  }
  
  /**
   * Добавляет файлы (алиас для addFilesToEditingService для совместимости)
   */
  async addFiles(files: File[]): Promise<void> {
    return this.addFilesToEditingService(files);
  }
  
  /**
   * Обновляет настройки файла
   */
  updateEditingFile(fileId: string, updates: Partial<FileState>): void {
    if (!this.editing.service) {
      return;
    }
    
    runInAction(() => {
      const file = this.editing.service!.files.find(f => f.id === fileId);
      if (file) {
        Object.assign(file, updates);
      }
    });
    
    this.saveState();
  }
  
  /**
   * Добавляет редактируемую услугу в корзину
   */
  addServiceToCart(): void {
    if (!this.editing.service || this.editing.service.files.length === 0) {
      return;
    }
    
    runInAction(() => {
      // Создаем копию услуги для корзины
      const cartService: ServiceState = observable({
        ...this.editing.service!,
        files: this.editing.service!.files.map(f => ({ ...f }))
      });
      
      // Проверяем, нет ли уже такой услуги в корзине
      const existingIndex = this.cart.services.findIndex(s => s.id === cartService.id);
      
      if (existingIndex >= 0) {
        // Обновляем существующую услугу
        this.cart.services[existingIndex] = cartService;
      } else {
        // Добавляем новую услугу
        this.cart.services.push(cartService);
      }
      
      // Пересчитываем корзину
      this.recalculateCart();
    });
    
    this.saveState();
  }
  
  /**
   * Удаляет услугу из корзины
   */
  removeServiceFromCart(serviceId: string): void {
    runInAction(() => {
      this.cart.services = this.cart.services.filter(s => s.id !== serviceId);
      
      // Если удалили редактируемую услугу, очищаем editing
      if (this.editing.service?.id === serviceId) {
        this.editing.service = null;
        this.editing.file = null;
      }
      
      this.recalculateCart();
    });
    
    this.saveState();
  }
  
  /**
   * Удаляет услугу из корзины (алиас для совместимости)
   */
  deleteService(service: ServiceState): void {
    this.removeServiceFromCart(service.id);
  }
  
  /**
   * Начинает редактирование услуги из корзины
   */
  editServiceFromCart(serviceId: string): void {
    const cartService = this.cart.services.find(s => s.id === serviceId);
    if (!cartService) {
      return;
    }
    
    runInAction(() => {
      // Создаем копию для редактирования
      const editingService: ServiceState = observable({
        ...cartService,
        files: cartService.files.map(f => ({ ...f }))
      });
      
      this.editing.service = editingService;
      this.editing.file = editingService.files[0] || null;
      // currentService и currentFile - это геттеры, которые возвращают editing.service и editing.file
    });
    
    this.saveState();
  }
  
  /**
   * Удаляет файл из редактируемой услуги
   */
  deleteFileFromEditingService(fileId: string): void {
    if (!this.editing.service) {
      return;
    }
    
    runInAction(() => {
      this.editing.service!.files = this.editing.service!.files.filter(f => f.id !== fileId);
      
      // Если удалили текущий файл, выбираем другой
      if (this.editing.file?.id === fileId) {
        if (this.editing.service!.files.length > 0) {
          this.editing.file = this.editing.service!.files[0];
        } else {
          this.editing.file = null;
        }
      }
    });
    
    this.saveState();
  }
  
  /**
   * Удаляет файл (алиас для deleteFileFromEditingService для совместимости)
   */
  deleteFile(file: FileState): void {
    this.deleteFileFromEditingService(file.id);
  }
  
  /**
   * Рассчитывает цену для редактируемой услуги
   */
  async calculatePrice(): Promise<void> {
    if (!this.editing.service || this.editing.service.files.length === 0) {
      return;
    }

    // Защита от одновременных вызовов
    if (this._isCalculatingPrice) {
      return;
    }

    runInAction(() => {
      this._isCalculatingPrice = true;
    });

    try {
      

      // Функция преобразования диапазона страниц в массив (как в старом проекте)
      const getPages = (range: string): string[] | "all" => {
        if (!range || !range.trim()) return "all";

        const result = range.split(",").flatMap((part) => {
          const trimmed = part.trim();
          if (!trimmed) return [];
          
          if (trimmed.includes("-")) {
            // Обрабатываем диапазон типа "1-3" -> ["1", "2", "3"]
            const [start, end] = trimmed.split("-").map(Number);
            if (isNaN(start) || isNaN(end) || start < 1 || end < start) {
              return [trimmed]; // Если диапазон некорректный, возвращаем как есть
            }
            return Array.from({ length: end - start + 1 }, (_, i) =>
              String(start + i)
            );
          }
          return [trimmed];
        });

        return result.length > 0 ? result : "all";
      };

      // Подготавливаем данные для запроса
      const files = this.editing.service.files.map(file => {
        const pagesRange = file.pagesRangeValid || file.pagesRange || "";
        // Преобразуем диапазон страниц в массив или "all"
        // Схема принимает: массив строк ИЛИ строку "all" (константа)
        const pages = getPages(pagesRange);
        
        // Убеждаемся, что colorType установлен (должен быть установлен при создании файла)
        const colorType = file.colorType;
        
        // Убеждаемся, что formatType установлен
        const formatType = file.formatType || "A4";
        
        // Проверяем, что colorType не undefined (может быть null, но не undefined)
        // colorType может быть ColorType | undefined, но schema ожидает string | null
        const colorValue: string | null = colorType ?? null;
        
        // Убеждаемся, что file_id всегда строка (API требует string)
        const fileId = String(file.id);
        
        return {
          file_id: fileId,
          copies: file.copiesCount,
          pages: pages,
          parameters: {
            color: colorValue,
            duplex: file.duplexOption,
            format: formatType
          },
          total_pages: file.totalPages
        };
      });

      const requestData = {
        service: {
          service_type: this.editing.service.type,
          files
        },
        machine_id: this.machineId
      };

      // Рассчитываем цену через FileService с проверкой ресурсов (новая ручка, без промокода)
      const response = await FileService.calculatePriceWithValidation(requestData);
      
      const filesData = response.files || null;

      runInAction(() => {
        // Обновляем цены файлов
        if (filesData && Array.isArray(filesData)) {
          // Создаем новый объект для правильной реактивности MobX
          const updatedPrices = { ...this.filePrices };
          
          filesData.forEach((filePrice: { file_id: string | number; total_price: number }) => {
            const fileId = String(filePrice.file_id); // Убеждаемся, что file_id строка
            updatedPrices[fileId] = filePrice.total_price;
            const file = this.editing.service?.files.find(f => String(f.id) === fileId);
            if (file) {
              file.price = filePrice.total_price;
            }
          });
          
          // Присваиваем новый объект для триггера реактивности
          // ВАЖНО: это НЕ должно триггерить autorun, так как мы не отслеживаем filePrices в autorun
          this.filePrices = updatedPrices;
        } else {
          console.error("[OrderState.calculatePrice] No files in response.data:", response);
        }

        // Сохраняем информацию о расчете цены с проверкой ресурсов (без промокода)
        this.priceCalculation = {
          resourcesSufficient: response.resources_sufficient,
          missingResources: response.missing_resources || [],
          resourcesDetails: response.resources_details || "",
        };

        // Обновляем previousPriceParams после успешного расчета, чтобы избежать повторных вызовов
        // Это особенно важно при прямом вызове calculatePrice (например, после добавления файлов)
        if (this.editing.service && this.editing.service.files.length > 0) {
          const filesParams = this.editing.service.files
            .map((file) => ({
              fileId: String(file.id),
              duplexOption: Boolean(file.duplexOption),
              formatType: String(file.formatType || ""),
              copiesCount: Number(file.copiesCount || 1),
              colorType: file.colorType ?? null,
              pagesRangeValid: file.pagesRangeValid ?? null
            }))
            .sort((a, b) => a.fileId.localeCompare(b.fileId));
          this.previousPriceParams = JSON.stringify(filesParams);
        }

        // Пересчитываем корзину
        this.recalculateCart();
      });
    } catch (error) {
      console.error("[OrderState.calculatePrice] Error calculating price:", error);
      
      // Правильно обрабатываем ошибку - показываем пользователю
      if (error instanceof HandleableError) {
        // Помечаем ошибку как обработанную и показываем пользователю
        error.handle();
        error.showError();
      } else if (error instanceof Error) {
        // Если это обычная ошибка, создаем HandleableError для показа
        const handleableError = new HandleableError(
          "Ошибка расчёта цены",
          error.message || "Не удалось рассчитать цену. Пожалуйста, попробуйте ещё раз."
        );
        handleableError.handle();
        handleableError.showError();
      }
      
      // Не пробрасываем ошибку дальше, так как это фоновый расчет цены
      // и не должен блокировать работу пользователя
    } finally {
      runInAction(() => {
        this._isCalculatingPrice = false;
      });
    }
  }

  /**
   * Пересчитывает цены корзины
   */
  recalculateCart(): void {
    runInAction(() => {
      // Пересчитываем каждую услугу
      this.cart.services.forEach(service => {
        // filePrice.total_price уже содержит цену с учетом копий (возвращается из API)
        service.totalPrice = service.files.reduce(
          (sum, file) => sum + (this.filePrices[file.id] || 0),
          0
        );
        // totalFiles - это количество файлов, а не копий
        service.totalFiles = service.files.length;
      });
      
      // Пересчитываем корзину
      // Используем totalPrice из cartCalculation если есть (с учетом промокода), иначе базовую цену
      const baseTotalPrice = this.cart.services.reduce(
        (sum, service) => sum + service.totalPrice,
        0
      );
      this.cart.totalPrice = this.cartCalculation?.totalPrice ?? baseTotalPrice;
      this.cart.totalServices = this.cart.services.length;
      this.cart.totalFiles = this.cart.services.reduce(
        (sum, service) => sum + service.totalFiles,
        0
      );
    });
  }
  
  /**
   * Рассчитывает корзину с применением промокода
   */
  async calculateCartWithPromo(): Promise<void> {
    if (this.cart.services.length === 0) {
      runInAction(() => {
        this.cartCalculation = null;
        this.recalculateCart();
      });
      return;
    }

    try {
      const services = this.cart.services.map(service => ({
        service_type: service.type,
        files: service.files.map(file => {
          const pagesRange = file.pagesRangeValid || file.pagesRange || "";
          const getPages = (range: string): string[] | "all" => {
            if (!range || !range.trim()) return "all";
            const result = range.split(",").flatMap((part) => {
              const trimmed = part.trim();
              if (!trimmed) return [];
              if (trimmed.includes("-")) {
                const [start, end] = trimmed.split("-").map(Number);
                if (isNaN(start) || isNaN(end) || start < 1 || end < start) {
                  return [trimmed];
                }
                return Array.from({ length: end - start + 1 }, (_, i) =>
                  String(start + i)
                );
              }
              return [trimmed];
            });
            return result.length > 0 ? result : "all";
          };
          const pages = getPages(pagesRange);

          return {
            file_id: String(file.id),
            copies: file.copiesCount,
            pages: pages,
            parameters: {
              color: file.colorType ?? null,
              duplex: file.duplexOption,
              format: file.formatType
            },
            total_pages: file.totalPages
          };
        })
      }));

      const requestData = {
        machine_id: this.machineId,
        services,
        use_free_prints: this.payWith.freePrints,
        use_points: this.payWith.points,
      };

      const response = await OrderService.calculateCart(requestData);
      
      console.log("[OrderState.calculateCartWithPromo] Response from backend:", {
        total_price: response.total_price,
        original_price: response.original_price,
        discount: response.discount,
        promo_code: response.promo_code
      });
      
      runInAction(() => {
        this.cartCalculation = {
          totalPrice: response.total_price,
          originalPrice: response.original_price,
          discount: response.discount,
          promoCode: response.promo_code,
          freePrintsUsed: response.free_prints_used ?? {},
          freePrintsAvailable: response.free_prints_available ?? {},
          freePrintsDiscount: response.free_prints_discount ?? 0,
          pointsUsed: response.points_used ?? 0,
          pointsAvailable: response.points_available ?? 0,
          minCardPayment: response.min_card_payment ?? 1,
        };
        this.recalculateCart();
        
        console.log("[OrderState.calculateCartWithPromo] After recalculate:", {
          cartTotalPrice: this.cart.totalPrice,
          cartCalculation: this.cartCalculation
        });
      });
    } catch (error) {
      console.error("[OrderState.calculateCartWithPromo] Error calculating cart:", error);
      runInAction(() => {
        this.cartCalculation = null;
        this.recalculateCart();
      });
    }
  }
  
  /**
   * Сохраняет состояние в localStorage
   */
  saveState(): void {
    try {
      const state = {
        cart: {
          services: this.cart.services.map(service => ({
            ...service,
            files: service.files.map(file => ({ ...file }))
          }))
        },
        editing: this.editing.service
          ? {
              service: {
                ...this.editing.service,
                files: this.editing.service.files.map(file => ({ ...file }))
              },
              fileId: this.editing.file?.id ?? null
            }
          : null,
        filePrices: { ...this.filePrices },
        totalPrice: this.cart.totalPrice
      };

      const storageKey = `${this.STORAGE_KEY_PREFIX}${this.machineId}`;
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.error("[OrderState.saveState] Failed to save state:", error);
    }
  }
  
  /**
   * Восстанавливает состояние из localStorage
   */
  private restoreState(): void {
    try {
      const storageKey = `${this.STORAGE_KEY_PREFIX}${this.machineId}`;
      const savedState = localStorage.getItem(storageKey);
      
      if (!savedState) {
        return;
      }
      
      this._isRestoringState = true;
      
      const state = JSON.parse(savedState);
      
      runInAction(() => {
        if (state.cart?.services) {
          this.cart.services = state.cart.services.map((service: any) =>
            observable({
              ...service,
              files: service.files.map((file: any) => observable({ ...file }))
            })
          );
        }

        if (state.editing?.service) {
          const restoredService = observable({
            ...state.editing.service,
            files: state.editing.service.files.map((file: any) => observable({ ...file }))
          });
          this.editing.service = restoredService;
          const fileId = state.editing.fileId;
          this.editing.file = fileId
            ? restoredService.files.find((f: FileState) => f.id === fileId) ?? restoredService.files[0] ?? null
            : restoredService.files[0] ?? null;
        }

        if (state.filePrices) {
          this.filePrices = { ...state.filePrices };
        }

        if (state.totalPrice !== undefined) {
          this.cart.totalPrice = state.totalPrice;
        }

        this.recalculateCart();
      });
      
      this._isRestoringState = false;
    } catch (error) {
      console.error("[OrderState.restoreState] Failed to restore state:", error);
    }
  }
  
  /**
   * Очищает состояние
   */
  clearState(): void {
    runInAction(() => {
      this.cart = {
        services: [],
        totalPrice: 0,
        totalServices: 0,
        totalFiles: 0
      };
      this.editing = {
        service: null,
        file: null
      };
      this.filePrices = {};
    });
    
    const storageKey = `${this.STORAGE_KEY_PREFIX}${this.machineId}`;
    localStorage.removeItem(storageKey);
  }
  
  /**
   * Очищает корзину после успешной отправки заказа
   */
  clearCart(): void {
    this.clearState();
  }
  
  /**
   * Создает заказ из корзины
   */
  async proceedPayment(): Promise<string> {
    console.log("[OrderState.proceedPayment] Starting order creation process");
    
    if (this.cart.services.length === 0) {
      console.error("[OrderState.proceedPayment] Cart is empty");
      throw new Error("Cart is empty");
    }

    // Преобразуем диапазон страниц в массив или "all"
    const getPages = (range: string): string[] | "all" => {
      if (!range || !range.trim()) return "all";

      const result = range.split(",").flatMap((part) => {
        const trimmed = part.trim();
        if (!trimmed) return [];
        
        if (trimmed.includes("-")) {
          const [start, end] = trimmed.split("-").map(Number);
          if (isNaN(start) || isNaN(end) || start < 1 || end < start) {
            return [trimmed];
          }
          return Array.from({ length: end - start + 1 }, (_, i) =>
            String(start + i)
          );
        }
        return [trimmed];
      });

      return result.length > 0 ? result : "all";
    };

    const services = this.cart.services.map(service => ({
      service_type: service.type,
      files: service.files.map(file => {
        const pagesRange = file.pagesRangeValid || file.pagesRange || "";
        const pages = getPages(pagesRange);
        
        // Убеждаемся, что file_id всегда строка (API требует string)
        const fileId = String(file.id);
        
        return {
          file_id: fileId,
          copies: file.copiesCount,
          pages: pages,
          parameters: {
            color: file.colorType ?? null,
            duplex: file.duplexOption,
            format: file.formatType || "A4"
          },
          total_pages: file.totalPages
        };
      })
    }));

    console.log("[OrderState.proceedPayment] Order data prepared:", {
      machine_id: this.machineId,
      services_count: services.length,
      services: services
    });

    const orderData = {
      machine_id: this.machineId,
      services,
      // Способ оплаты едет и в расчёт, и в создание: иначе заказ создастся по
      // одним правилам, а человек видел цену по другим.
      use_free_prints: this.payWith.freePrints,
      use_points: this.payWith.points,
    };

    try {
      // Шаг 1: Финальный расчет заказа перед созданием (согласно новому сценарию бэкенда)
      console.log("[OrderState.proceedPayment] Calculating final order price...");
      const calculateResponse = await OrderService.calculateOrder(orderData);
      
      console.log("[OrderState.proceedPayment] Order calculation response:", {
        total_price: calculateResponse.total_price,
        discount: calculateResponse.discount,
        available: calculateResponse.available
      });

      // Шаг 2: Создание заказа после успешного расчета
      console.log("[OrderState.proceedPayment] Creating order...");
      const response = await OrderService.createOrder(orderData);

      console.log("[OrderState.proceedPayment] Order creation response:", response);

      if (!response?.status || response.status !== "success") {
        console.error("[OrderState.proceedPayment] Order creation failed:", response);
        throw new Error("Failed to create order - invalid response");
      }

      console.log("[OrderState.proceedPayment] Order created successfully. Waiting for order_number via WebSocket...");

      // Очищаем корзину после успешного создания заказа
      this.clearCart();

      // order_number будет получен через WebSocket уведомление
      // Возвращаем пустую строку, так как order_number придет через WebSocket
      return "";
    } catch (error) {
      console.error("[OrderState.proceedPayment] Order creation failed:", error);
      
      // Улучшаем сообщения об ошибках для пользователя
      if (error instanceof Error) {
        if (error.message.includes("timeout")) {
          throw new Error("Превышено время ожидания. Операция заняла слишком много времени. Попробуйте ещё раз через несколько минут.");
        } else if (error.message.includes("500") || error.message.includes("Internal Server Error")) {
          throw new Error("Ошибка сервера при создании заказа. Попробуйте ещё раз через несколько минут.");
        } else if (error.message.includes("Network Error")) {
          throw new Error("Ошибка сети. Проверьте подключение к интернету и попробуйте ещё раз.");
        }
      }
      
      throw error;
    }
  }
  
  dispose(): void {
    if (this.saveStateTimeout) {
      clearTimeout(this.saveStateTimeout);
      this.saveStateTimeout = null;
    }
    
    this.saveState();
    this.autoruns.forEach(dispose => dispose());
  }
}

